import {
  AreaId,
  ConfigurationValues as ConfigurationsType,
  ConfigurationValues,
  getDefaultConfiguration,
  isOrderEditable,
  resolvePositionConfigurations,
} from '@cutr/constants/windows/windows';
import {
  AREA_CONFIGURATIONS_OPTIONS,
  ConfigurationKey,
  COVERS_CONFIGURATION_KEYS,
  DECORATIVE_CONFIGURATION_KEYS,
  FITTING_CONFIGURATION_KEYS,
  GEOMETRY_CONFIGURATION_KEYS,
  Label as LabelType,
  STANDARD_CONFIGURATION_KEYS,
} from '@cutr/constants/windows/windows-configurations.ts';
import { useFlag } from '@unleash/proxy-client-react';
import cn from 'classnames';
import React, { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { ConfirmationDialog } from '@/blocks/ConfirmationDialog';
import { Modal } from '@/blocks/Modal';
import { onFocusSelect } from '@/hooks';
import { Button } from '@/primitives/Button';
import { Checkbox } from '@/primitives/Checkbox';
import * as Icons from '@/primitives/Icons';
import { Icon } from '@/primitives/Icons';
import { Input } from '@/primitives/Input';
import { Quantity } from '@/primitives/Quantity';
import { useToggle } from '@/utils/hooks';
import { Label } from '@/windows/primitives/Label';
import { Select, SelectItem } from '@/windows/primitives/Select';

import { usePositionStore } from '../../api/positionStore';
import { usePositionAutosave } from '../../api/useAutosave';
import { useWindowStore } from '../../api/windowStore';
import {
  useDeletePosition,
  useDuplicatePosition,
} from '../../queries/position/crud';
import { PresetSelect } from '../PresetSelect';
import { WindowEditor } from '../renderer/WindowEditor';
import {
  ConfigurationDisplay,
  PositionLevelConfigurations,
  Subheader,
  unboxLabel,
} from './Configurations';
import configStyles from './Configurations.module.css';
import {
  ConfigurationsProvider,
  useConfigurations,
} from './ConfigurationsContext';
import styles from './PositionConfiguration.module.css';
import { AreaSashbarsInput } from './primitives/SashbarsConfig';

export const PositionConfiguration = () => {
  const { presetId } = usePositionStore();
  const { status } = useWindowStore();

  if (presetId === 'none') {
    if (!isOrderEditable(status)) {
      return null;
    }

    return <PresetSelect />;
  }

  return <PositionConfigurationsContainer />;
};

const PositionConfigurationsContainer = () => {
  const { t } = useTranslation();
  const {
    windowSpec,
    presetId,
    selectedAreaId,
    setPresetId,
    configurations,
    setConfigurations,
  } = usePositionStore();
  const { status, configurations: orderConfigurations } = useWindowStore();
  const { positionId } = useParams();
  return (
    <section className="positionConfigurationLayout">
      {windowSpec && presetId !== 'none' && (
        <div className="stack">
          <div className="flexAlign">
            <h2>{t('reseller.orderConfig.windowEditorTitle')}</h2>
            {isOrderEditable(status) && (
              <Button
                variant="secondary"
                style={{
                  backgroundColor: 'var(--gray-1)',
                }}
                icon={<Icon icon={<Icons.Cogwheel />} />}
                onClick={() => {
                  setPresetId('none');
                }}
              >
                {t('reseller.orderConfig.changePreset')}
              </Button>
            )}
          </div>
          <WindowEditor
            windowSpec={windowSpec}
            presetId={presetId}
            readonly={!isOrderEditable(status)}
          />
        </div>
      )}
      {!windowSpec && <div />}
      <ConfigurationsProvider
        key={`position-configurations-${positionId}-${selectedAreaId}`}
        configurations={configurations as ConfigurationValues}
        inheritedConfigurations={orderConfigurations}
        level={selectedAreaId ? 'area' : 'position'}
        onConfigurationChange={(configurations) => {
          setConfigurations(configurations);
        }}
      >
        <div className={styles.editorLayout}>
          {selectedAreaId ? <AreaEditor /> : <PositionEditor />}
        </div>
      </ConfigurationsProvider>
      <PositionAutosaver />
    </section>
  );
};

const PositionAutosaver = () => {
  usePositionAutosave();
  return null;
};

const PositionTitle = ({ isEditable }: { isEditable: boolean }) => {
  const [isEditing, setIsEditing] = useState(false);
  const { title, setTitle } = usePositionStore();

  const inputId = useId();

  useEffect(() => {
    if (isEditing) {
      document.getElementById(inputId)?.focus();
    }
  }, [isEditing, inputId]);

  if (isEditing) {
    return (
      <Input
        id={inputId}
        defaultValue={title}
        className={styles.positionTitle}
        onChange={(e) => setTitle(e.currentTarget.value)}
        onFocus={onFocusSelect}
        disabled={!isEditable}
        onBlur={() => setIsEditing(false)}
        maxLength={30}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            setIsEditing(false);
            e.preventDefault();
            return false;
          }
        }}
      />
    );
  } else {
    return (
      <div className={cn(styles.positionTitle, 'flexAlign gap-xxs')}>
        <h3 onClick={() => isEditable && setIsEditing(true)}>{title}</h3>
        {isEditable && (
          <Button
            style={{
              color: 'var(--primary)',
            }}
            variant="icon"
            icon={<Icon size={2.5} icon={<Icons.Edit />} />}
            onClick={() => setIsEditing(true)}
          />
        )}
      </div>
    );
  }
};

const PositionEditor = () => {
  const { t } = useTranslation();
  const { positionId } = useParams();
  const { quantity, setQuantity } = usePositionStore();

  const { status } = useWindowStore();

  if (!positionId) {
    return null;
  }

  const isEditable = isOrderEditable(status);

  return (
    <>
      <div className="opposites">
        <PositionTitle isEditable={isEditable} />
        <Quantity
          key={`position-quantity-${positionId}`}
          value={quantity}
          label={t('reseller.orderConfig.quantity')}
          onFocus={onFocusSelect}
          onChange={setQuantity}
          disabled={!isEditable}
        />
      </div>
      {isEditable && <Actions />}
      <div>
        <PositionLevelConfigurations
          renderMode={isEditable ? 'form' : 'readonly'}
        />
      </div>
    </>
  );
};

const Actions = () => {
  const { t } = useTranslation();
  const { onDuplicatePosition } = useDuplicatePosition();
  const { onDeletePosition } = useDeletePosition();
  const [isOpen, toggleModal] = useToggle(false);

  return (
    <div className="stack">
      <CustomizePosition />
      <div className="flexAlign">
        <Button
          icon={<Icon icon={<Icons.Duplicate />} />}
          variant="secondary"
          className="full"
          onClick={onDuplicatePosition}
        >
          {t('reseller.orderConfig.duplicate')}
        </Button>

        <Button
          icon={<Icon icon={<Icons.Trash />} />}
          variant="secondary"
          className="full delete"
          onClick={toggleModal}
        >
          {t('cutlists.actions.delete')}
        </Button>
      </div>

      <ConfirmationDialog
        onClose={toggleModal}
        title={t('reseller.orderConfig.deletePositionTitle')}
        onConfirm={async () => {
          await onDeletePosition();
          toggleModal();
        }}
        isOpen={isOpen}
        variant="alert"
      >
        <p>{t('reseller.orderConfig.deletePositionDescription')}</p>
      </ConfirmationDialog>
    </div>
  );
};

const CustomizePosition = () => {
  const { t } = useTranslation();
  const [isOpen, toggleModal] = useToggle(false);

  return (
    <>
      <Button
        variant="secondary"
        className="full"
        onClick={toggleModal}
        icon={<Icon icon={<Icons.SlidersHorizontal />} />}
      >
        {t('reseller.orderConfig.customizePosition')}
      </Button>
      {isOpen && (
        <Modal
          title={t('reseller.customizePosition.modal.title')}
          isOpen={isOpen}
          handleClose={toggleModal}
        >
          <CustomizePositionConfigurationModal toggleModal={toggleModal} />
        </Modal>
      )}
    </>
  );
};

const CustomizePositionConfigurationModal = ({
  toggleModal,
}: {
  toggleModal: () => void;
}) => {
  const { t: modalT } = useTranslation(undefined, {
    keyPrefix: 'reseller.customizePosition.modal',
  });

  const { configurations: positionConfigurations, dispatch } =
    useConfigurations();
  const { t } = useTranslation();

  const isBeamWidthEnabled = useFlag('custom-beam-widths');
  const coversKeys = COVERS_CONFIGURATION_KEYS;
  const geometryKeys = GEOMETRY_CONFIGURATION_KEYS.filter(
    (key) =>
      (key !== 'frameBeamWidth' && key !== 'sashBeamWidth') ||
      isBeamWidthEnabled
  );
  const fittingKeys = FITTING_CONFIGURATION_KEYS.filter(
    (key) => key === 'coverStrips' || key === 'plasteringStrips'
  );
  const decorativeKeys = DECORATIVE_CONFIGURATION_KEYS.filter(
    (key) => key === 'sashbars'
  );

  const [overridenKeys, setOverridenKeys] = React.useState<
    (keyof ConfigurationsType)[]
  >(Object.keys(positionConfigurations) as (keyof ConfigurationsType)[]);

  const { configurations: orderConfigurations } = useWindowStore();

  const mergedConfigurations = resolvePositionConfigurations({
    orderConfigurations,
    positionConfigurations,
  });

  const saveOverrides = () => {
    const newPositionConfigurations = Object.fromEntries(
      overridenKeys.map((key) => [key, mergedConfigurations[key]])
    );

    const oldConfigurationKeys = Object.keys(
      positionConfigurations
    ) as ConfigurationKey[];
    const newConfigurationKeys = Object.keys(
      newPositionConfigurations
    ) as ConfigurationKey[];

    const toDelete = oldConfigurationKeys.filter(
      (key) => !newConfigurationKeys.includes(key)
    );

    const toAdd = newConfigurationKeys.filter(
      (key) => !oldConfigurationKeys.includes(key)
    );

    dispatch({
      type: 'CUSTOMIZE_POSITION_CONFIGURATIONS',
      toDelete: toDelete,
      toAdd: toAdd,
      orderConfigurations: orderConfigurations,
    });

    toggleModal();
  };

  return (
    <div className={styles.customizePositionConfigurationModal}>
      <p>{modalT('description')}</p>
      <div className={styles.configurationContainer}>
        <h3>{modalT('standardConfigurations')}</h3>
        {STANDARD_CONFIGURATION_KEYS.map((key) => {
          const value = mergedConfigurations[key];
          if (value === undefined) {
            return null;
          }
          const isOverriden = overridenKeys.includes(key);

          return (
            <React.Fragment key={key}>
              <Checkbox
                checked={isOverriden}
                onChange={() =>
                  isOverriden
                    ? setOverridenKeys(overridenKeys.filter((k) => k !== key))
                    : setOverridenKeys([...overridenKeys, key])
                }
              >
                {t(`windows.configurations.${key}.label`)}
              </Checkbox>
              <ConfigurationDisplay configurationKey={key} value={value} />
            </React.Fragment>
          );
        })}

        <h3>{modalT('geometryConfigurations')}</h3>
        {geometryKeys.map((key) => {
          const value = mergedConfigurations[key];
          const isOverriden = overridenKeys.includes(key);

          return (
            <React.Fragment key={key}>
              <Checkbox
                checked={isOverriden}
                onChange={() =>
                  isOverriden
                    ? setOverridenKeys(overridenKeys.filter((k) => k !== key))
                    : setOverridenKeys([...overridenKeys, key])
                }
              >
                {t(`windows.configurations.${key}.label`)}
              </Checkbox>
              <ConfigurationDisplay configurationKey={key} value={value} />
            </React.Fragment>
          );
        })}

        <h3>{modalT('coversConfigurations')}</h3>
        {coversKeys.map((key) => {
          const value = mergedConfigurations[key];
          const isOverriden = overridenKeys.includes(key);

          return (
            <React.Fragment key={key}>
              <Checkbox
                checked={isOverriden}
                onChange={() =>
                  isOverriden
                    ? setOverridenKeys(overridenKeys.filter((k) => k !== key))
                    : setOverridenKeys([...overridenKeys, key])
                }
              >
                {t(`windows.configurations.${key}.label`)}
              </Checkbox>
              <ConfigurationDisplay configurationKey={key} value={value} />
            </React.Fragment>
          );
        })}

        {fittingKeys.length > 0 && (
          <>
            <h3>{modalT('fittingConfigurations')}</h3>
            {fittingKeys.map((key) => {
              const value = mergedConfigurations[key];
              const isOverriden = overridenKeys.includes(key);

              return (
                <React.Fragment key={key}>
                  <Checkbox
                    className={styles.checkboxWrapper}
                    checked={isOverriden}
                    onChange={() =>
                      isOverriden
                        ? setOverridenKeys(
                            overridenKeys.filter((k) => k !== key)
                          )
                        : setOverridenKeys([...overridenKeys, key])
                    }
                  >
                    {t(`windows.configurations.${key}.label`)}
                  </Checkbox>
                  <ConfigurationDisplay configurationKey={key} value={value} />
                </React.Fragment>
              );
            })}
          </>
        )}

        {decorativeKeys.length > 0 && (
          <>
            <h3>{modalT('decorativeConfigurations')}</h3>
            {decorativeKeys.map((key) => {
              const value = mergedConfigurations[key];
              const isOverriden = overridenKeys.includes(key);

              return (
                <React.Fragment key={key}>
                  <Checkbox
                    className={styles.checkboxWrapper}
                    checked={isOverriden}
                    onChange={() =>
                      isOverriden
                        ? setOverridenKeys(
                            overridenKeys.filter((k) => k !== key)
                          )
                        : setOverridenKeys([...overridenKeys, key])
                    }
                  >
                    {t(`windows.configurations.${key}.label`)}
                  </Checkbox>
                  <ConfigurationDisplay configurationKey={key} value={value} />
                </React.Fragment>
              );
            })}
          </>
        )}
      </div>
      <div className={styles.buttonContainer}>
        <Button onClick={saveOverrides}>{modalT('save')}</Button>
      </div>
    </div>
  );
};

export const LabelValue = ({ value }: { value: { label: LabelType } }) => {
  return unboxLabel(value.label);
};

export const AreaEditor = () => {
  const { t } = useTranslation();

  const { selectedAreaId, windowSpec, setAreaSashType, toggleSelectedAreaId } =
    usePositionStore();
  const { status } = useWindowStore();

  const sashTypeOptions = AREA_CONFIGURATIONS_OPTIONS.sashType.options;

  if (!selectedAreaId || !windowSpec) {
    return null;
  }

  const areaConfigurations = windowSpec.areaConfigurations[selectedAreaId];
  const hasAreaSashbarsOverride = areaConfigurations?.sashbars !== undefined;
  const showSashbars = hasAreaSashbarsOverride;
  const isEditable = isOrderEditable(status);

  return (
    <>
      <div className="opposites">
        <h3>{t('reseller.areaConfig.title')}</h3>
        <Button
          variant="secondary"
          icon={<Icon icon={<Icons.ArrowLeft />} />}
          onClick={() => toggleSelectedAreaId(selectedAreaId)}
        >
          {t('reseller.areaConfig.back')}
        </Button>
      </div>
      {isEditable && <CustomizeArea areaId={selectedAreaId} />}
      <div className={cn(configStyles.configurations, configStyles.inputs)}>
        <Label id="sashType" htmlFor="sashType">
          {t(`windows.configurations.sashType.label`)}
        </Label>
        <Select
          aria-labelledby="sashType"
          id="sashType"
          items={sashTypeOptions}
          selectedKey={areaConfigurations?.sashType}
          onSelectionChange={(selected) => {
            const value = sashTypeOptions.find(
              (option) => option.value === selected
            )!;
            setAreaSashType(selectedAreaId, value.value);
          }}
        >
          {(option) => (
            <SelectItem
              id={option.value.toString()}
              textValue={option.value.toString()}
            >
              <LabelValue value={option} />
            </SelectItem>
          )}
        </Select>
        {showSashbars && (
          <>
            <Subheader title={t('reseller.orderConfig.sashbars')} />
            <AreaSashbarsInput areaId={selectedAreaId} />
          </>
        )}
      </div>
    </>
  );
};

const CustomizeArea = ({ areaId }: { areaId: AreaId }) => {
  const { t } = useTranslation();
  const [isOpen, toggleModal] = useToggle(false);

  return (
    <>
      <Button
        variant="secondary"
        className="full"
        onClick={toggleModal}
        icon={<Icon icon={<Icons.SlidersHorizontal />} />}
      >
        {t('reseller.orderConfig.customizeArea')}
      </Button>
      {isOpen && (
        <Modal
          title={t('reseller.customizeArea.modal.title')}
          isOpen={isOpen}
          handleClose={toggleModal}
        >
          <CustomizeAreaConfigurationModal
            areaId={areaId}
            toggleModal={toggleModal}
          />
        </Modal>
      )}
    </>
  );
};

const CustomizeAreaConfigurationModal = ({
  areaId,
  toggleModal,
}: {
  areaId: AreaId;
  toggleModal: () => void;
}) => {
  const { t: modalT } = useTranslation(undefined, {
    keyPrefix: 'reseller.customizeArea.modal',
  });
  const { t } = useTranslation();
  const { windowSpec, setAreaSashbars } = usePositionStore();
  const { configurations: positionConfigurations } = useConfigurations();
  const { configurations: orderConfigurations } = useWindowStore();

  const areaSashbars = windowSpec?.areaConfigurations[areaId]?.sashbars;
  const resolvedSashbars =
    areaSashbars ??
    positionConfigurations.sashbars ??
    orderConfigurations.sashbars;

  const initialSashbarsEnabled = areaSashbars !== undefined;
  const [sashbarsEnabled, setSashbarsEnabled] = useState(
    initialSashbarsEnabled
  );

  const save = () => {
    if (sashbarsEnabled !== initialSashbarsEnabled) {
      setAreaSashbars(
        areaId,
        sashbarsEnabled
          ? (resolvedSashbars ?? getDefaultConfiguration('sashbars'))
          : undefined
      );
    }
    toggleModal();
  };

  return (
    <div className={styles.customizePositionConfigurationModal}>
      <p>{modalT('description')}</p>
      <div className={styles.configurationContainer}>
        <h3>{modalT('decorativeConfigurations')}</h3>
        <Checkbox
          className={styles.checkboxWrapper}
          checked={sashbarsEnabled}
          onChange={() => setSashbarsEnabled((prev) => !prev)}
        >
          {t('windows.configurations.sashbars.label')}
        </Checkbox>
        <ConfigurationDisplay
          configurationKey="sashbars"
          value={resolvedSashbars}
        />
      </div>
      <div className={styles.buttonContainer}>
        <Button onClick={save}>{modalT('save')}</Button>
      </div>
    </div>
  );
};
